import { clsx } from 'clsx'
import * as React from 'react'
import { useFormControlProps } from '../Form/useFormControlProps'

import styles from './Input.module.css'

type InputProps = Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'size' | 'prefix' | 'suffix'
> & {
  /**
   * Size of the input
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Prefix element to render before the input
   */
  prefix?: React.ReactNode
  /**
   * Suffix element to render after the input
   */
  suffix?: React.ReactNode
  /** Custom styles for the input container */
  style?: React.CSSProperties
}

/**
 * Input is a text input field component that supports various sizes, prefix/suffix elements, and accessibility features.
 *
 * It wraps the native HTML input element with consistent styling and behavior across the design system.
 * The component supports all standard input attributes including validation states (aria-invalid), disabled state,
 * and can be customized with prefix/suffix elements for icons or text.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Input placeholder="Enter text" />
 *
 * // With prefix icon
 * <Input prefix={<SearchIcon />} placeholder="Search..." />
 *
 * // With error state
 * <Input aria-invalid={true} aria-describedby="error-message" />
 *
 * // Different sizes
 * <Input size="small" placeholder="Small input" />
 * ```
 *
 * @css `--input-affix-color` — Overrides the color of prefix and suffix elements.
 * Defaults to `var(--color-text-subtle)`. Set this custom property via `className` or `style`
 * to change the affix color without affecting the input text color.
 *
 * ```tsx
 * // Override via CSS class
 * // .active { --input-affix-color: var(--color-text-brand); }
 * <Input className={styles.active} prefix={<SearchIcon />} />
 *
 * // Override via inline style
 * <Input style={{ '--input-affix-color': 'var(--color-text-brand)' } as React.CSSProperties} prefix={<SearchIcon />} />
 * ```
 *
 * @css `--input-placeholder-color` — Overrides the color of the placeholder text.
 * Defaults to `var(--color-text-disabled)`. Set this custom property via `className` or `style`
 * when the placeholder needs more contrast against the surface behind the input.
 *
 * ```tsx
 * <Input
 *   style={{ '--input-placeholder-color': 'var(--color-text-subtle)' } as React.CSSProperties}
 *   placeholder="New password"
 * />
 * ```
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, size = 'medium', prefix, suffix, style, ...props }: InputProps,
    forwardedRef,
  ) => {
    const controlProps = useFormControlProps(props, { native: true })
    return (
      <div
        className={clsx(
          styles.base,
          size !== 'medium' && styles[size],
          className,
        )}
        style={style}
      >
        {prefix && <div className={styles.prefix}>{prefix}</div>}
        <input
          type="text"
          {...controlProps}
          className={styles.input}
          ref={forwardedRef}
        />
        {suffix && <div className={styles.suffix}>{suffix}</div>}
      </div>
    )
  },
)

Input.displayName = 'Input'

export { Input }
export type { InputProps }
